import React, { useMemo } from 'react';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Search,
  MessageSquare,
  FileText,
  Hash,
  Reply,
  Power
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { type AutoReply } from '@/services/autoReplies.service';
import { Organization } from '@/services/organizations.service';
import { formatDateTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface AutoReplyDataTableProps {
  autoReplies: AutoReply[];
  organizations: Organization[];
  isLoading: boolean;
  onEdit: (autoReply: AutoReply) => void;
  onDelete: (organizationId: string) => void;
  onToggleStatus: (autoReply: AutoReply) => void;
  isDeletePending?: boolean;
}

export function AutoReplyDataTable({ 
  autoReplies, 
  organizations,
  isLoading, 
  onEdit, 
  onDelete,
  onToggleStatus
}: AutoReplyDataTableProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);

  // Helper to get organization name
  const getOrgName = (orgId: string) => {
    // First try the name from autoReply if available, otherwise look up in organizations list
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Unbekannte Organisation';
  };

  // Filter auto-replies
  const filteredAutoReplies = useMemo(() => {
    return autoReplies.filter(autoReply => {
      const orgName = autoReply.organization_name || getOrgName(autoReply.organization_id);
      const matchesSearch = 
        orgName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        autoReply.template_html.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [autoReplies, searchTerm, organizations]);

  // Pagination
  const totalPages = Math.ceil(filteredAutoReplies.length / itemsPerPage);
  const paginatedAutoReplies = filteredAutoReplies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800" />
          <p className="text-sm text-slate-500">{t('common:x1.loadingAutoReplies', 'Auto-Antworten werden geladen...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex flex-1 items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('common:x2.searchOrg', 'Organisation suchen...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 bg-white border-slate-200 focus-visible:ring-slate-200"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border-t border-slate-100">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-6 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[250px]">
                {t('common:field.organization', 'Organisation')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('common:x1.contentPreview', 'Inhalt (Vorschau)')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[120px]">
                {t('common:field.status', 'Status')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[150px]">
                {t('common:x2.updated', 'Aktualisiert')}
              </th>
              <th className="py-3 px-6 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[100px]">
                {t('common:field.actions', 'Aktionen')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedAutoReplies.map((autoReply) => {
              const orgName = autoReply.organization_name || getOrgName(autoReply.organization_id);
              
              return (
                <tr key={autoReply.id} className="group hover:bg-slate-50/50 transition-colors">
                  <td className="py-4 px-6 align-top">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-semibold text-xs border border-slate-200">
                        {orgName.substring(0, 2).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-900">{orgName}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 align-top">
                    <div className="max-w-xl text-sm text-slate-600 line-clamp-2 leading-relaxed" 
                         dangerouslySetInnerHTML={{ __html: autoReply.template_html.replace(/<[^>]*>?/gm, ' ') }} />
                  </td>
                  <td className="py-4 px-4 align-top">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={autoReply.is_active}
                        onCheckedChange={() => onToggleStatus(autoReply)}
                        className="scale-75"
                      />
                      <Badge 
                        variant="secondary" 
                        className={`text-[10px] h-5 px-1.5 font-medium ${
                          autoReply.is_active 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}
                      >
                        {autoReply.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </div>
                  </td>
                  <td className="py-4 px-4 align-top">
                    <div className="text-xs text-slate-500">
                      {formatDateTime(autoReply.updated_at)}
                    </div>
                  </td>
                  <td className="py-4 px-6 align-top text-right">
                    <div className="flex items-center justify-end gap-1 opacity-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                        onClick={() => onEdit(autoReply)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-2xl border-0 shadow-xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('common:x2.deleteAutoReply', 'Auto-Antwort löschen')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              Sind Sie sicher, dass Sie die Auto-Antwort für "{orgName}" löschen möchten? 
                              Diese Aktion kann nicht rückgängig gemacht werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-lg border-slate-200">{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => onDelete(autoReply.organization_id)}
                              className="bg-red-600 hover:bg-red-700 rounded-lg text-white"
                            >
                              {t('common:action.delete', 'Löschen')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredAutoReplies.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-4">
              <Reply className="h-6 w-6 text-slate-300" />
            </div>
            <h3 className="text-sm font-medium text-slate-900">{t('common:x2.noAutoRepliesFound', 'Keine Auto-Antworten gefunden')}</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">
              {searchTerm 
                ? 'Versuchen Sie es mit anderen Suchbegriffen.'
                : 'Erstellen Sie eine automatische Antwort für Ihre Organisationen.'}
            </p>
          </div>
        )}

        {/* Pagination Footer */}
        {filteredAutoReplies.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/30">
            <div className="text-xs text-slate-500">
              {t('common:x1.showing', 'Zeige')} {(currentPage - 1) * itemsPerPage + 1} bis {Math.min(currentPage * itemsPerPage, filteredAutoReplies.length)} von {filteredAutoReplies.length} Einträgen
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">{t('common:action.back', 'Zurück')}</span>
                ←
              </Button>
              <div className="text-xs font-medium text-slate-700">
                {t('common:field.page', 'Seite')} {currentPage} von {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">{t('common:action.next', 'Weiter')}</span>
                →
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
